import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../../admin/api";
import { toast } from "sonner";
import { Save } from "lucide-react";

export default function AdminContactInfo() {
  const [form, setForm] = useState({
    phone: "",
    email: "",
    address: "",
    hours_de: "",
    hours_en: "",
    hours_es: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get("/content/settings");
        if (!mounted) return;
        if (data.contact) {
          setForm((f) => ({ ...f, ...data.contact }));
        } else {
          // sensible defaults from current static content
          setForm({
            phone: "+41 52 551 01 40",
            email: "kontakt@schweizer-planmanufaktur.ch",
            address: "Gründenstrasse 82, 8247 Flurlingen",
            hours_de: "Mo – Fr · 07:00 – 18:00 · Notfall 24/7",
            hours_en: "Mon – Fri · 07:00 – 18:00 · 24/7 hotline",
            hours_es: "Lu – Vi · 07:00 – 18:00 · 24/7",
          });
        }
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fehler beim Laden");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/content/contact", form);
      toast.success("Kontaktdaten gespeichert.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-10 text-[#5A6270] font-mono-tech">LÄDT…</div>;

  return (
    <div data-testid="admin-contact-info" className="p-6 md:p-10 max-w-3xl">
      <div className="font-mono-tech text-[#1d428a] mb-2">
        <span className="inline-block w-2 h-2 bg-[#df4661] mr-3 align-middle" />
        ADMIN · KONTAKTDATEN
      </div>
      <h1 className="font-display text-4xl md:text-5xl text-[#0A0A0A]">Kontaktdaten.</h1>
      <p className="mt-3 text-[#5A6270] text-sm md:text-base max-w-2xl">
        Adresse, Telefon, E-Mail und Öffnungszeiten der Webseite. Öffnungszeiten pro Sprache pflegbar.
      </p>

      <div className="mt-8 space-y-px bg-[#1d428a]/15 border border-[#1d428a]/15">
        <Row label="Telefon" testid="admin-ci-phone" value={form.phone} onChange={update("phone")} placeholder="+41 52 …" />
        <Row label="E-Mail" testid="admin-ci-email" type="email" value={form.email} onChange={update("email")} placeholder="kontakt@…" />
        <Row label="Adresse" testid="admin-ci-address" value={form.address} onChange={update("address")} placeholder="Strasse Nr., PLZ Ort" />
        <Row label="Öffnungszeiten (DE)" testid="admin-ci-hours-de" value={form.hours_de} onChange={update("hours_de")} />
        <Row label="Öffnungszeiten (EN)" testid="admin-ci-hours-en" value={form.hours_en} onChange={update("hours_en")} />
        <Row label="Öffnungszeiten (ES)" testid="admin-ci-hours-es" value={form.hours_es} onChange={update("hours_es")} />
      </div>

      <button
        type="button"
        onClick={save}
        disabled={saving}
        data-testid="admin-ci-save"
        className="mt-8 inline-flex items-center gap-3 bg-[#1d428a] text-white font-mono-tech px-6 py-4 hover:bg-[#df4661] disabled:opacity-60 transition-colors"
      >
        <Save size={15} />
        {saving ? "SPEICHERN…" : "ÄNDERUNGEN SPEICHERN"}
      </button>
    </div>
  );
}

const Row = ({ label, value, onChange, type = "text", testid, placeholder }) => (
  <div className="bg-white p-5">
    <label className="font-mono-tech text-[#df4661] flex items-center gap-3">
      <span>{label.toUpperCase()}</span>
    </label>
    <input
      data-testid={testid}
      type={type}
      value={value || ""}
      onChange={onChange}
      placeholder={placeholder}
      className="mt-3 w-full bg-transparent border-0 border-b border-[#1d428a]/30 focus:border-[#df4661] outline-none py-2 font-display-mid text-lg text-[#0A0A0A]"
    />
  </div>
);
